import { useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { BarcodeField } from "../BarcodeField";
import { useT } from "../../i18n/LanguageProvider";

/**
 * G-59 — barcode capture at first receipt. Shown on a pending line whose
 * item has no barcode yet: scan the box (or type it) and the item learns it.
 * G-61: a code another item also carries is fine — manufacturers reuse an
 * EAN across variants — so the only refusal is overwriting a different
 * barcode this item already has.
 */
interface Props {
  lineId: string;
  itemName: string;
  disabled?: boolean;
  /** Render the collapsed trigger as a small inline link (kiosk rows). */
  compact?: boolean;
  /**
   * G-59: the last scan that matched nothing on this delivery. Offered as an
   * explicit suggestion, never prefilled — it came from whatever box was
   * last scanned, not necessarily from this item's box, and a silently
   * filled field one tap away from Save is how the wrong barcode lands on
   * the wrong item.
   */
  suggestedCode?: string;
  onCapture: (barcode: string) => Promise<void>;
}

export function CaptureLineBarcode({
  lineId,
  itemName,
  disabled,
  suggestedCode,
  onCapture,
  compact = false,
}: Props) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (barcode: string) => {
    if (!barcode.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onCapture(barcode.trim());
      setOpen(false);
      setValue("");
    } catch (err: unknown) {
      setError(errorText(err, "receiving.couldNotSaveBarcode"));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        /*
          `compact` is a small inline annotation rather than a full-height
          button. On the kiosk receiving list almost every line lacks a barcode,
          so a 44px button per line added ~70px to EVERY row and roughly doubled
          the length of the delivery — measured at 145px per row against a 72px
          design. The full-size control stays for the CRM, where it sits alone.
        */
        className={compact ? "sf-inline-link" : "button plain"}
        disabled={disabled}
        onClick={() => {
          setValue("");
          setOpen(true);
        }}
        title={t("receiving.noBarcodeTitle", { item: itemName })}
      >
        {compact
          ? t("receiving.noBarcodeCompact")
          : t("receiving.addBarcodeFromBox")}
      </button>
    );
  }

  return (
    <div className="capture-line-barcode">
      <BarcodeField
        id={`capture-barcode-${lineId}`}
        label={t("receiving.barcodeOnBoxFor", { item: itemName })}
        value={value}
        onChange={setValue}
        onScanDetected={(scanned) => void save(scanned)}
        hint={t("receiving.barcodeSavedHint")}
      />
      {suggestedCode && suggestedCode !== value ? (
        <button
          type="button"
          className="button plain"
          disabled={busy}
          onClick={() => setValue(suggestedCode)}
        >
          {t("receiving.useLastScan", { code: suggestedCode })}
        </button>
      ) : null}
      {error ? <div className="alert error">{error}</div> : null}
      <div className="button-row" style={{ gap: "var(--space-2)" }}>
        <button
          type="button"
          className="button"
          disabled={busy || !value.trim()}
          onClick={() => void save(value)}
        >
          {busy ? t("belowFloor.saving") : t("receiving.saveBarcode")}
        </button>
        <button
          type="button"
          className="button plain"
          disabled={busy}
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}
